import { useReducer, useEffect, useCallback } from "react"
import { produce } from "immer"

const ACTION_TYPES = {
  SET_LOCAL_QUESTION: "SET_LOCAL_QUESTION",
  SET_ERROR_STATE: "SET_ERROR_STATE",
  SET_CONTENT_WIDTH: "SET_CONTENT_WIDTH",
  SET_IS_DRAGGING: "SET_IS_DRAGGING",
  ASK_QUESTION_INITIALIZE: "ASK_QUESTION_INITIALIZE",
  ASK_QUESTION_SUCCESS: "ASK_QUESTION_SUCCESS",
  ASK_QUESTION_ERROR: "ASK_QUESTION_ERROR",
  CLEAR_ALL: "CLEAR_ALL",
}

const initialState = {
  results: null,
  lastUpdated: null,
  hasAskedQuestion: false,
  errorState: null,
  contentWidth: 400,
  isDragging: false,
  localQuestion: "",
}

const handleSetLocalQuestion = (state, payload) => {
  return produce(state, draft => {
    draft.localQuestion = payload
  })
}

const handleSetErrorState = (state, payload) => {
  return produce(state, draft => {
    draft.errorState = payload
  })
}

const handleSetContentWidth = (state, payload) => {
  return produce(state, draft => {
    draft.contentWidth = payload
  })
}

const handleSetIsDragging = (state, payload) => {
  return produce(state, draft => {
    draft.isDragging = payload
  })
}

const handleAskQuestionInitialize = (state, payload) => {
  return produce(state, draft => {
    draft.results = null
    draft.lastUpdated = null
    draft.hasAskedQuestion = true
    draft.askedQuestion = payload.question
    draft.errorState = null
  })
}

const handleAskQuestionSuccess = (state, payload) => {
  return produce(state, draft => {
    draft.results = payload.result
    draft.lastUpdated = new Date()
    const hasNoResults = !payload.result?.records?.length && !payload.result?.explainSteps && !payload.result?.columns?.length
    draft.errorState = hasNoResults ? { type: payload.noResultsErrorType } : null
  })
}

const handleAskQuestionError = (state, payload) => {
  return produce(state, draft => {
    draft.errorState = {
      type: payload.error,
      message: payload.message || null,
    }
  })
}

const handleClearAll = (state) => {
  return produce(state, draft => {
    draft.localQuestion = ""
    draft.askedQuestion = ""
    draft.results = null
    draft.lastUpdated = null
    draft.hasAskedQuestion = false
    draft.errorState = null
  })
}

function quickAnswersReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_LOCAL_QUESTION:
      return handleSetLocalQuestion(state, action.payload)

    case ACTION_TYPES.SET_ERROR_STATE:
      return handleSetErrorState(state, action.payload)

    case ACTION_TYPES.SET_CONTENT_WIDTH:
      return handleSetContentWidth(state, action.payload)

    case ACTION_TYPES.SET_IS_DRAGGING:
      return handleSetIsDragging(state, action.payload)

    case ACTION_TYPES.ASK_QUESTION_INITIALIZE:
      return handleAskQuestionInitialize(state, action.payload)

    case ACTION_TYPES.ASK_QUESTION_SUCCESS:
      return handleAskQuestionSuccess(state, action.payload)

    case ACTION_TYPES.ASK_QUESTION_ERROR:
      return handleAskQuestionError(state, action.payload)

    case ACTION_TYPES.CLEAR_ALL:
      return handleClearAll(state)

    default:
      return state
  }
}

// Uses useReducer instead of multiple useState for related state management, atomic updates,
// predictable transitions, and centralized logic for complex quick answers state machine
export function useQuickAnswersState(contextQuestion) {
  const [state, dispatch] = useReducer(quickAnswersReducer, {
    ...initialState,
    localQuestion: contextQuestion,
  })

  useEffect(() => {
    dispatch({ type: ACTION_TYPES.SET_LOCAL_QUESTION, payload: contextQuestion })
  }, [contextQuestion])

  const actions = {
    setLocalQuestion: useCallback((question) => dispatch({ type: ACTION_TYPES.SET_LOCAL_QUESTION, payload: question }), [dispatch]),
    setErrorState: useCallback((errorState) => dispatch({ type: ACTION_TYPES.SET_ERROR_STATE, payload: errorState }), [dispatch]),
    setContentWidth: useCallback((width) => dispatch({ type: ACTION_TYPES.SET_CONTENT_WIDTH, payload: width }), [dispatch]),
    setIsDragging: useCallback((isDragging) => dispatch({ type: ACTION_TYPES.SET_IS_DRAGGING, payload: isDragging }), [dispatch]),
    askQuestionInitialize: useCallback((question) => dispatch({ type: ACTION_TYPES.ASK_QUESTION_INITIALIZE, payload: { question } }), [dispatch]),
    askQuestionSuccess: useCallback((result, noResultsErrorType = "NO_RESULTS") => dispatch({ type: ACTION_TYPES.ASK_QUESTION_SUCCESS, payload: { result, noResultsErrorType } }), [dispatch]),
    askQuestionError: useCallback((error, message) => dispatch({ type: ACTION_TYPES.ASK_QUESTION_ERROR, payload: { error, message } }), [dispatch]),
  }

  const clearAll = useCallback(() => { dispatch({ type: ACTION_TYPES.CLEAR_ALL }) }, [dispatch])

  return {
    ...state,
    ...actions,
    clearAll,
  }
}
